import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Wallet, Plus, Minus, Trash2, Save, ArrowLeft, Download, FileText } from "lucide-react";
import { Link } from "react-router-dom";

const PERIODS = [["monthly", "Monthly"], ["quarterly", "Quarterly"], ["trimester", "Every 4 months"], ["annual", "Annual"]];
const STREAM_COLORS = { sales: "#8a3ffc", activation: "#0f62fe", re_activation: "#08bdba", service_charge: "#ff7eb6" };
const up = (n) => Math.ceil(Number(n || 0));
const money = (n) => `$${up(n).toLocaleString()}`;

export default function DablConsole() {
  const [period, setPeriod] = useState("monthly");
  const [ov, setOv] = useState(null);
  const [sales, setSales] = useState(null);
  const [msg, setMsg] = useState("");
  const [newCat, setNewCat] = useState({ name: "", pct: "" });
  const [edit, setEdit] = useState({});
  const [txns, setTxns] = useState(null);
  const [rt, setRt] = useState(null);            // refund target transaction
  const [rf, setRf] = useState({ amount: "", revoke: false, reason: "" });
  const loadTxns = () => client.get("/admin/transactions").then((r) => setTxns(r.data.transactions)).catch(() => {});
  useEffect(() => { loadTxns(); }, []);
  const openRefund = (t) => { setRt(t); setRf({ amount: String(t.refundable), revoke: false, reason: "" }); };
  const submitRefund = async () => {
    const amt = parseFloat(rf.amount);
    if (!(amt > 0) || amt > rt.refundable + 0.001) { setMsg(`Enter an amount between 0.01 and ${rt.refundable}.`); return; }
    try {
      const r = await client.post(`/admin/transactions/${rt.id}/refund`, { amount: amt, revoke_access: rf.revoke, reason: rf.reason });
      setMsg(r.data.fully_refunded ? "✓ Full refund issued." : `✓ Partial refund of $${amt.toFixed(2)} issued.`);
      setRt(null); loadTxns(); load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const load = () => client.get(`/dabl/overview?period=${period}`).then((r) => setOv(r.data)).catch((e) => setMsg(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);
  useEffect(() => { client.get("/dabl/sales-breakdown").then((r) => setSales(r.data)).catch(() => {}); }, []);

  const entry = async (cat, type) => {
    const amt = window.prompt(`${type === "draw" ? "Draw from" : "Add to"} "${cat.name}" — amount:`);
    if (!amt) return;
    const note = window.prompt("Note (optional):") || "";
    try { await client.post("/dabl/entry", { category_id: cat.id, type, amount: parseFloat(amt), note }); setMsg(`${type === "draw" ? "Draw" : "Add"} recorded.`); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const savePct = async (cat) => {
    const v = edit[cat.id];
    if (v === undefined) return;
    try { await client.put(`/dabl/categories/${cat.id}`, { pct: parseFloat(v) }); setEdit((s) => { const n = { ...s }; delete n[cat.id]; return n; }); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const addCat = async () => {
    if (!newCat.name || newCat.pct === "") { setMsg("Enter a name and %."); return; }
    try { await client.post("/dabl/categories", { name: newCat.name, pct: parseFloat(newCat.pct) }); setNewCat({ name: "", pct: "" }); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const delCat = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Its manual Draw/Add history is removed too.`)) return;
    try { await client.delete(`/dabl/categories/${cat.id}`); load(); } catch (e) { setMsg(apiErr(e)); }
  };
  const exportFile = async (fmt) => {
    try {
      setMsg(`Preparing ${fmt.toUpperCase()} export…`);
      const r = await client.get(`/dabl/export?period=${period}&fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `dabl_${period}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setMsg(`${fmt.toUpperCase()} downloaded.`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  if (!ov) return <div className="p-8" data-testid="dabl-loading">Loading DABL…</div>;

  const chartData = ov.buckets.map((b) => {
    const row = { label: b.label };
    ov.streams.forEach((s) => { row[s.key] = b.streams[s.key]?.net || 0; });
    return row;
  });

  return (
    <div className="space-y-8 p-2" data-testid="dabl-console">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/app/enabler" className="flex items-center gap-1 text-sm text-[var(--primary)]" data-testid="dabl-back"><ArrowLeft size={15} /> Master Enabler</Link>
        <Wallet size={22} className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl">DABL Console <span className="text-sm opacity-60">· Draw · Add · Balance Logger</span></h1>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="dabl-period"
          className="ms-auto rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5 text-sm">
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => exportFile("csv")} data-testid="dabl-export-csv" title="Export CSV"
          className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><Download size={14} /> CSV</button>
        <button onClick={() => exportFile("pdf")} data-testid="dabl-export-pdf" title="Export PDF"
          className="flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-white lift"><FileText size={14} /> PDF</button>
      </div>
      {msg && <p className="text-sm opacity-70" data-testid="dabl-msg">{msg}</p>}

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3" data-testid="dabl-totals">
        <div className="rounded-xl glass p-4"><div className="font-head text-2xl">{money(ov.totals.gross)}</div><div className="text-xs opacity-60">Gross revenue</div></div>
        <div className="rounded-xl glass p-4"><div className="font-head text-2xl">{money(ov.totals.net)}</div><div className="text-xs opacity-60">Net revenue (allocatable)</div></div>
        <div className="rounded-xl glass p-4"><div className="font-head text-2xl">{money(ov.totals.tax)}</div><div className="text-xs opacity-60">Tax pool (handled by Stripe)</div></div>
      </div>

      {/* Revenue streams */}
      <div>
        <h2 className="font-head text-lg mb-3">Revenue streams — net allocated by {PERIODS.find((p) => p[0] === period)[1].toLowerCase()}</h2>
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="dabl-streams">
          {ov.streams.map((s) => (
            <div key={s.key} className="rounded-xl glass p-4" data-testid={`dabl-stream-${s.key}`}>
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STREAM_COLORS[s.key] }} /><span className="text-sm">{s.label}</span></div>
              <div className="font-head text-xl mt-1">{money(ov.stream_totals[s.key].net)}</div>
              <div className="text-xs opacity-50">gross {money(ov.stream_totals[s.key].gross)}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl glass p-4" style={{ height: 320 }} data-testid="dabl-stream-chart">
          {chartData.length === 0 ? <p className="text-sm opacity-60">No revenue recorded yet.</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} /><Legend />
                {ov.streams.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={STREAM_COLORS[s.key]} />)}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Discounted app sales (platform-wide) */}
      <div data-testid="dabl-sales">
        <h2 className="font-head text-lg mb-3">Discounted sales — by application &amp; full suite</h2>
        {!sales ? <p className="text-sm opacity-60">Loading…</p> : sales.totals.units === 0 ? (
          <p className="text-sm opacity-60" data-testid="dabl-sales-empty">No self-serve sales recorded yet (test-mode sales excluded).</p>
        ) : (
          <div className="overflow-x-auto rounded-xl glass p-4">
            <table className="w-full text-sm" data-testid="dabl-sales-table">
              <thead><tr className="text-start opacity-60">
                <th className="pb-2">Product</th><th className="pb-2 text-end">Sales</th><th className="pb-2 text-end">Apps unlocked</th>
                <th className="pb-2 text-end">List</th><th className="pb-2 text-end">Discount given</th><th className="pb-2 text-end">Net revenue</th>
              </tr></thead>
              <tbody>
                {sales.rows.filter((r) => r.units > 0).map((r) => (
                  <tr key={r.app_type} className="border-t border-black/5 dark:border-white/5" data-testid={`dabl-sales-${r.app_type}`}>
                    <td className="py-1.5">{r.label}</td>
                    <td className="py-1.5 text-end">{r.units}</td>
                    <td className="py-1.5 text-end">{r.apps_unlocked}</td>
                    <td className="py-1.5 text-end">{money(r.list)}</td>
                    <td className="py-1.5 text-end text-amber-600">{money(r.discount)}</td>
                    <td className="py-1.5 text-end font-medium">{money(r.gross)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-black/10 dark:border-white/10 font-semibold" data-testid="dabl-sales-total">
                  <td className="py-1.5">Total</td>
                  <td className="py-1.5 text-end">{sales.totals.units}</td>
                  <td className="py-1.5 text-end">{sales.totals.apps_unlocked}</td>
                  <td className="py-1.5 text-end">{money(sales.totals.list)}</td>
                  <td className="py-1.5 text-end text-amber-600">{money(sales.totals.discount)}</td>
                  <td className="py-1.5 text-end">{money(sales.totals.gross)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Categories */}
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="font-head text-lg">Allocation categories</h2>
          <span className={`text-xs ${ov.vendor_total_pct === 100 ? "opacity-60" : "text-amber-500"}`} data-testid="dabl-total-pct">
            Allocated: {ov.vendor_total_pct}%{ov.vendor_total_pct !== 100 ? " (not 100%)" : ""}
          </span>
        </div>
        <div className="overflow-x-auto rounded-xl glass">
          <table className="w-full text-sm" data-testid="dabl-categories">
            <thead className="text-start opacity-60"><tr>
              <th className="p-3">Category</th><th className="p-3">%</th><th className="p-3">Allocated (net)</th>
              <th className="p-3">Add</th><th className="p-3">Draw</th><th className="p-3">Balance</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {ov.categories.map((c) => {
                const bal = ov.balances[c.id] || {};
                return (
                  <tr key={c.id} className="border-t border-black/5 dark:border-white/5" data-testid={`dabl-cat-${c.id}`}>
                    <td className="p-3 font-medium">{c.name}{c.is_tax && <span className="ms-2 rounded bg-[var(--primary)]/10 px-1.5 py-0.5 text-[10px] text-[var(--primary)]">TAX</span>}</td>
                    <td className="p-3">
                      {c.is_tax ? `${c.pct}%` : (
                        <span className="flex items-center gap-1">
                          <input type="number" defaultValue={c.pct} onChange={(e) => setEdit((s) => ({ ...s, [c.id]: e.target.value }))}
                            data-testid={`dabl-pct-${c.id}`} className="w-16 rounded border border-black/15 dark:border-white/15 bg-transparent px-1 py-0.5" />
                          {edit[c.id] !== undefined && <button onClick={() => savePct(c)} data-testid={`dabl-savepct-${c.id}`} className="text-[var(--primary)]"><Save size={14} /></button>}
                        </span>
                      )}
                    </td>
                    <td className="p-3">{money(bal.allocated_all)}</td>
                    <td className="p-3 text-emerald-600">{money(bal.add)}</td>
                    <td className="p-3 text-[#DA1E28]">{money(bal.draw)}</td>
                    <td className="p-3 font-head">{money(bal.balance)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => entry(c, "add")} data-testid={`dabl-add-${c.id}`} title="Add" className="rounded-full bg-emerald-500/15 p-1 text-emerald-600"><Plus size={14} /></button>
                        <button onClick={() => entry(c, "draw")} data-testid={`dabl-draw-${c.id}`} title="Draw" className="rounded-full bg-[#DA1E28]/15 p-1 text-[#DA1E28]"><Minus size={14} /></button>
                        {!c.is_tax && <button onClick={() => delCat(c)} data-testid={`dabl-del-${c.id}`} title="Delete" className="text-black/40 hover:text-[#DA1E28]"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Add category */}
        {ov.categories.filter((c) => !c.is_tax).length < 20 && (
          <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="dabl-add-cat">
            <input placeholder="New category" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="dabl-newcat-name"
              className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-2 text-sm" />
            <input type="number" placeholder="%" value={newCat.pct} onChange={(e) => setNewCat({ ...newCat, pct: e.target.value })} data-testid="dabl-newcat-pct"
              className="w-20 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-2 text-sm" />
            <button onClick={addCat} data-testid="dabl-newcat-add" className="rounded-md bg-[var(--primary)] px-3 py-2 text-sm text-white lift">Add category</button>
          </div>
        )}
      </div>

      {/* Transactions & Refunds */}
      <div className="rounded-xl glass p-4" data-testid="dabl-transactions">
        <div className="flex items-center gap-2">
          <h2 className="font-head text-lg">Transactions &amp; Refunds</h2>
          <span className="text-xs opacity-60">paid &amp; refunded · latest 500</span>
        </div>
        {!txns ? <p className="mt-2 text-sm opacity-60">Loading…</p> : txns.length === 0 ? (
          <p className="mt-2 text-sm opacity-60" data-testid="dabl-txns-empty">No paid transactions yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-start opacity-60">
                <th className="py-1 pe-3">Date</th><th className="pe-3">Buyer</th><th className="pe-3">Product</th>
                <th className="pe-3 text-end">Amount</th><th className="pe-3 text-end">Refunded</th>
                <th className="pe-3">Status</th><th></th></tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-black/5 dark:border-white/5" data-testid={`txn-row-${t.id}`}>
                    <td className="py-1.5 pe-3 whitespace-nowrap">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="pe-3">{t.email || "—"}{t.test && <span className="ms-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">TEST</span>}</td>
                    <td className="pe-3">{t.product}</td>
                    <td className="pe-3 text-end">${t.amount.toFixed(2)}</td>
                    <td className="pe-3 text-end">{t.refunded_amount > 0 ? `$${t.refunded_amount.toFixed(2)}` : "—"}</td>
                    <td className="pe-3">
                      {t.refund_status === "full" ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] text-red-700">Refunded</span>
                        : t.refund_status === "partial" ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">Partial</span>
                        : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">Paid</span>}
                      {t.access_revoked && <span className="ms-1 text-[10px] opacity-50">access revoked</span>}
                    </td>
                    <td className="text-end">
                      {t.refundable > 0.001 && (
                        <button onClick={() => openRefund(t)} data-testid={`txn-refund-${t.id}`}
                          className="rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">Refund</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rt && (
          <div className="mt-4 rounded-lg border border-[var(--primary)]/40 p-3" data-testid="refund-dialog">
            <div className="text-sm font-medium">Refund — {rt.product} <span className="opacity-60">({rt.email || "—"})</span></div>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="text-xs opacity-70">Amount (max ${rt.refundable.toFixed(2)})
                <input type="number" step="0.01" value={rf.amount} onChange={(e) => setRf({ ...rf, amount: e.target.value })} data-testid="refund-amount"
                  className="mt-1 block w-32 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs opacity-70">Reason (optional)
                <input value={rf.reason} onChange={(e) => setRf({ ...rf, reason: e.target.value })} data-testid="refund-reason"
                  className="mt-1 block w-56 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" />
              </label>
              <label className="flex items-center gap-2 text-xs" data-testid="refund-revoke-label">
                <input type="checkbox" checked={rf.revoke} onChange={(e) => setRf({ ...rf, revoke: e.target.checked })} data-testid="refund-revoke" />
                Revoke access (leave unchecked to comp)
              </label>
              <button onClick={submitRefund} data-testid="refund-submit" className="rounded-md bg-[#DA1E28] px-3 py-1.5 text-sm text-white lift">Issue refund</button>
              <button onClick={() => setRt(null)} data-testid="refund-cancel" className="rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm">Cancel</button>
            </div>
            <p className="mt-2 text-[11px] opacity-50">This creates a real Stripe refund on the buyer's payment. Refunded amounts automatically net out of DABL revenue.</p>
          </div>
        )}
      </div>
    </div>
  );
}
